import React from 'react';
import { useLocation } from 'react-router-dom';

// Log saat file utils.js ter-load
//console.log('🟡 [utils.js] ===== utils.js FILE LOADED =====');
//console.log('🟡 [utils.js] File loaded at:', new Date().toISOString());
//console.log('🟡 [utils.js] React version:', React.version);
//console.log('🟡 [utils.js] useLocation available:', typeof useLocation);

export function useQuery() {
  //console.log('🟡 [useQuery] Function called');
  const { search } = useLocation()
  //console.log('🟡 [useQuery] Location search:', search);

  return React.useMemo(() => {
    //console.log('🟡 [useQuery] useMemo executing, search:', search);
    let searchParams = new URLSearchParams(search)
    let params = {}
    for (let pair of searchParams.entries()) {
      params[pair[0]] = pair[1]
    }
    return params
  }, [search])
}

export function formatIDR(num = 0) {
  const formatter = new Intl.NumberFormat('id-ID', {
    style: 'currency',
    currency: 'IDR',
    maximumFractionDigits: 0,
  })

  let formattedNum = formatter.format(num);

  // Remove space between Rp and the number
  formattedNum = formattedNum.replace(/\s/g, '');

  return formattedNum;
}

/**
 * Extract Google Analytics parameters from URL
 * @param {string} url - URL string to parse (optional, defaults to current URL)
 * @returns {object} Object containing GA parameters
 */
export function extractGAParameters(url = null) {
  //console.log('🟣 [GA Extract] ===== extractGAParameters CALLED =====');
  //console.log('🟣 [GA Extract] Input URL:', url);
  //console.log('🟣 [GA Extract] window.location.href:', window.location.href);

  try {
    const urlToParse = url || window.location.href;
    //console.log('🟣 [GA Extract] URL to parse:', urlToParse);

    const urlObj = new URL(urlToParse);
    //console.log('🟣 [GA Extract] URL object created:', urlObj);
    //console.log('🟣 [GA Extract] URL search:', urlObj.search);
    //console.log('🟣 [GA Extract] URL search params string:', urlObj.search);

    const params = new URLSearchParams(urlObj.search);
    //console.log('🟣 [GA Extract] URLSearchParams created');
    //console.log('🟣 [GA Extract] All URL params:', Object.fromEntries(params.entries()));

    const extractedParams = {
      _gl: params.get('_gl') || null,
      gclid: params.get('gclid') || null,
      utm_source: params.get('utm_source') || null,
      utm_medium: params.get('utm_medium') || null,
      utm_campaign: params.get('utm_campaign') || null,
      utm_term: params.get('utm_term') || null,
      utm_content: params.get('utm_content') || null,
      pid: params.get('pid') || null,
      billingcycle: params.get('billingcycle') || null,
    };

    //console.log('🟣 [GA Extract] Extracted parameters:', extractedParams);
    //console.log('🟣 [GA Extract] _gl value:', extractedParams._gl);
    //console.log('🟣 [GA Extract] _gl exists?', !!extractedParams._gl);
    //console.log('🟣 [GA Extract] ===== EXTRACTION SUCCESS =====');

    return extractedParams;
  } catch (error) {
    console.error('🔴 [GA Extract] ===== EXTRACTION ERROR =====');
    console.error('🔴 [GA Extract] Error extracting GA parameters:', error);
    console.error('🔴 [GA Extract] Error name:', error.name);
    console.error('🔴 [GA Extract] Error message:', error.message);
    console.error('🔴 [GA Extract] Error stack:', error.stack);
    // Fallback: try parsing from search string directly
    if (url) {
      try {
        const params = new URLSearchParams(url);
        return {
          _gl: params.get('_gl') || null,
          gclid: params.get('gclid') || null,
          utm_source: params.get('utm_source') || null,
          utm_medium: params.get('utm_medium') || null,
          utm_campaign: params.get('utm_campaign') || null,
          utm_term: params.get('utm_term') || null,
          utm_content: params.get('utm_content') || null,
          pid: params.get('pid') || null,
          billingcycle: params.get('billingcycle') || null,
        };
      } catch (e) {
        return {
          _gl: null,
          gclid: null,
          utm_source: null,
          utm_medium: null,
          utm_campaign: null,
          utm_term: null,
          utm_content: null,
          pid: null,
          billingcycle: null,
        };
      }
    }
    return {
      _gl: null,
      gclid: null,
      utm_source: null,
      utm_medium: null,
      utm_campaign: null,
      utm_term: null,
      utm_content: null,
      pid: null,
      billingcycle: null,
    };
  }
}

/**
 * Get or create session ID for tracking
 * @returns {string} Session ID
 */
export function getSessionId() {
  let sessionId = sessionStorage.getItem('ga_session_id');
  if (!sessionId) {
    sessionId = 'ga_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
    sessionStorage.setItem('ga_session_id', sessionId);
  }
  return sessionId;
}

/**
 * Save Google Analytics tracking data to backend
 * @param {object} gaData - Google Analytics data to save
 * @returns {Promise<object>} Response from API
 */
export async function saveGoogleAnalytics(gaData) {
  //console.log('🔵 [GA Tracking] ===== saveGoogleAnalytics FUNCTION CALLED =====');
  //console.log('🔵 [GA Tracking] Input gaData:', gaData);
  //console.log('🔵 [GA Tracking] Current URL:', window.location.href);
  //console.log('🔵 [GA Tracking] Document Referrer:', document.referrer);
  //console.log('🔵 [GA Tracking] User Agent:', navigator.userAgent);
  //console.log('🔵 [GA Tracking] REACT_APP_BASE_API:', process.env.REACT_APP_BASE_API);

  try {
    const baseApi = process.env.REACT_APP_BASE_API || '';
    const apiUrl = `${baseApi}order/apis/saveGoogleAnalytics.php`;

    //console.log('🔵 [GA Tracking] ===== REQUEST DETAILS =====');
    //console.log('🔵 [GA Tracking] Full API URL:', apiUrl);
    //console.log('🔵 [GA Tracking] Base API:', baseApi);
    //console.log('🔵 [GA Tracking] API Path:', 'order/apis/saveGoogleAnalytics.php');
    //console.log('🔵 [GA Tracking] Request Method: POST');

    const requestBody = {
      session_id: getSessionId(),
      gl_parameter: gaData._gl,
      source_url: document.referrer || null,
      landing_url: gaData.landing_url || window.location.href,
      current_url: window.location.href,
      user_agent: navigator.userAgent,
      utm_source: gaData.utm_source,
      utm_medium: gaData.utm_medium,
      utm_campaign: gaData.utm_campaign,
      utm_term: gaData.utm_term,
      utm_content: gaData.utm_content,
      gclid: gaData.gclid,
      product_id: gaData.pid ? parseInt(gaData.pid) : null,
      billing_cycle: gaData.billingcycle || null,
    };

    //console.log('🔵 [GA Tracking] Request Body:', requestBody);
    //console.log('🔵 [GA Tracking] Request Body (JSON):', JSON.stringify(requestBody, null, 2));
    // console.log('🔵 [GA Tracking] Request Headers:', {
    //   'Content-Type': 'application/json',
    //   'Accept': 'application/json',
    //   'X-Requested-With': 'XMLHttpRequest'
    // });

    const requestOptions = {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify(requestBody)
    };

    //console.log('🔵 [GA Tracking] ===== SENDING FETCH REQUEST =====');
    //console.log('🔵 [GA Tracking] Fetch options:', requestOptions);
    //console.log('🔵 [GA Tracking] Timestamp:', new Date().toISOString());

    const response = await fetch(apiUrl, requestOptions);

    //console.log('🔵 [GA Tracking] ===== RESPONSE RECEIVED =====');
    //console.log('🔵 [GA Tracking] Response Status:', response.status);
    //console.log('🔵 [GA Tracking] Response Status Text:', response.statusText);
    //console.log('🔵 [GA Tracking] Response OK:', response.ok);
    //console.log('🔵 [GA Tracking] Response Headers:', Object.fromEntries(response.headers.entries()));
    //console.log('🔵 [GA Tracking] Response URL:', response.url);
    //console.log('🔵 [GA Tracking] Response Type:', response.type);
    //console.log('🔵 [GA Tracking] Response Redirected:', response.redirected);

    const responseText = await response.text();
    //console.log('🔵 [GA Tracking] Response Text (raw):', responseText);
    //console.log('🔵 [GA Tracking] Response Text Length:', responseText.length);

    let data;
    try {
      data = JSON.parse(responseText);
      //console.log('🔵 [GA Tracking] Response JSON Parsed:', data);
    } catch (parseError) {
      console.error('🔴 [GA Tracking] JSON Parse Error:', parseError);
      console.error('🔴 [GA Tracking] Failed to parse response as JSON');
      console.error('🔴 [GA Tracking] Response was:', responseText);
      throw new Error(`Failed to parse response as JSON: ${parseError.message}`);
    }

    //console.log('🔵 [GA Tracking] ===== REQUEST COMPLETED =====');
    //console.log('🔵 [GA Tracking] Final Response Data:', data);
    //console.log('🔵 [GA Tracking] Response Code:', data.code);
    //console.log('🔵 [GA Tracking] Response Message:', data.message);

    return data;
  } catch (error) {
    console.error('🔴 [GA Tracking] ===== ERROR OCCURRED =====');
    console.error('🔴 [GA Tracking] Error Name:', error.name);
    console.error('🔴 [GA Tracking] Error Message:', error.message);
    console.error('🔴 [GA Tracking] Error Stack:', error.stack);
    console.error('🔴 [GA Tracking] Error Full Object:', error);
    console.error('🔴 [GA Tracking] Error Type:', typeof error);

    // Check if it's a network error
    if (error instanceof TypeError && error.message.includes('fetch')) {
      console.error('🔴 [GA Tracking] Network Error Detected - Request may not have reached server');
      console.error('🔴 [GA Tracking] Possible causes:');
      console.error('🔴 [GA Tracking] - CORS issue');
      console.error('🔴 [GA Tracking] - Network connectivity problem');
      console.error('🔴 [GA Tracking] - Wrong URL');
      console.error('🔴 [GA Tracking] - Server not responding');
    }

    return {
      code: 500,
      message: 'Failed to save Google Analytics data',
      error: error.message
    };
  }
}

/**
 * Hook untuk tracking Google Analytics dari URL
 * @returns {object} GA tracking data dan function untuk save
 */
export function useGoogleAnalyticsTracking() {
  //console.log('🟢 [GA Tracking Hook] ===== useGoogleAnalyticsTracking FUNCTION CALLED =====');
  //console.log('🟢 [GA Tracking Hook] Function called at:', new Date().toISOString());

  const location = useLocation();
  //console.log('🟢 [GA Tracking Hook] useLocation() called, location:', location);

  const [gaData, setGaData] = React.useState(null);
  const [isSaved, setIsSaved] = React.useState(false);
  const savedRef = React.useRef(false);

  //console.log('🟢 [GA Tracking Hook] State initialized');
  //console.log('🟢 [GA Tracking Hook] gaData:', gaData);
  //console.log('🟢 [GA Tracking Hook] isSaved:', isSaved);
  //console.log('🟢 [GA Tracking Hook] savedRef.current:', savedRef.current);

  React.useEffect(() => {
    //console.log('🟢 [GA Tracking Hook] ===== useGoogleAnalyticsTracking EFFECT TRIGGERED =====');
    //console.log('🟢 [GA Tracking Hook] Location object:', location);
    //console.log('🟢 [GA Tracking Hook] Location pathname:', location.pathname);
    //console.log('🟢 [GA Tracking Hook] Location search:', location.search);
    //console.log('🟢 [GA Tracking Hook] Location hash:', location.hash);
    //console.log('🟢 [GA Tracking Hook] window.location.href:', window.location.href);
    //console.log('🟢 [GA Tracking Hook] window.location.search:', window.location.search);
    //console.log('🟢 [GA Tracking Hook] window.location.pathname:', window.location.pathname);
    //console.log('🟢 [GA Tracking Hook] Saved ref current:', savedRef.current);

    // Extract GA parameters from current URL first
    //console.log('🟢 [GA Tracking Hook] Extracting GA parameters from URL...');
    const params = extractGAParameters();
    //console.log('🟢 [GA Tracking Hook] Extracted parameters from URL:', params);

    // Check sessionStorage for saved _gl parameter (in case it was lost during navigation)
    const savedGlParam = sessionStorage.getItem('ga_gl_parameter');
    const alreadySaved = sessionStorage.getItem('ga_tracking_saved') === 'true';
    const savedGlTimestamp = sessionStorage.getItem('ga_gl_timestamp');
    //console.log('🟢 [GA Tracking Hook] Saved _gl from sessionStorage:', savedGlParam);
    //console.log('🟢 [GA Tracking Hook] Saved _gl timestamp:', savedGlTimestamp);
    //console.log('🟢 [GA Tracking Hook] Already saved?', alreadySaved);

    // If _gl exists in URL, check if it's different from saved one
    if (params._gl) {
      //console.log('🟢 [GA Tracking Hook] _gl found in URL:', params._gl);

      // Check if this is a different _gl parameter than what was saved
      if (savedGlParam && savedGlParam !== params._gl) {
        //console.log('🟢 [GA Tracking Hook] ⚠️ Different _gl detected! Old:', savedGlParam, 'New:', params._gl);
        //console.log('🟢 [GA Tracking Hook] Resetting savedRef to allow saving new _gl');
        savedRef.current = false;
        sessionStorage.removeItem('ga_tracking_saved');
        sessionStorage.removeItem('ga_tracking_saved_at');
      }

      // Save new _gl to sessionStorage
      //console.log('🟢 [GA Tracking Hook] Saving _gl to sessionStorage');
      sessionStorage.setItem('ga_gl_parameter', params._gl);
      sessionStorage.setItem('ga_gl_timestamp', Date.now().toString());
    } else if (savedGlParam) {
      // If _gl not in URL but exists in sessionStorage, use it
      //console.log('🟢 [GA Tracking Hook] _gl not in URL, but found in sessionStorage, using saved value');
      params._gl = savedGlParam;
      // Also try to get other saved params
      const savedParams = sessionStorage.getItem('ga_all_params');
      if (savedParams) {
        try {
          const parsed = JSON.parse(savedParams);
          Object.assign(params, parsed);
          //console.log('🟢 [GA Tracking Hook] Restored all params from sessionStorage:', params);
        } catch (e) {
          console.error('🔴 [GA Tracking Hook] Error parsing saved params:', e);
        }
      }

      // If already saved with this _gl, mark savedRef
      if (alreadySaved) {
        savedRef.current = true;
        //console.log('🟢 [GA Tracking Hook] Marking savedRef.current = true because already saved with this _gl');
      }
    } else {
      // No _gl anywhere
      //console.log('🟢 [GA Tracking Hook] ⚠️ No _gl found in URL or sessionStorage');
      // Reset savedRef if no _gl at all
      if (!params._gl) {
        savedRef.current = false;
        //console.log('🟢 [GA Tracking Hook] Resetting savedRef.current = false because no _gl found');
      }
    }

    //console.log('🟢 [GA Tracking Hook] Final params after sessionStorage check:', params);
    //console.log('🟢 [GA Tracking Hook] _gl parameter:', params._gl);
    //console.log('🟢 [GA Tracking Hook] _gl exists?', !!params._gl);
    //console.log('🟢 [GA Tracking Hook] savedRef.current?', savedRef.current);
    //console.log('🟢 [GA Tracking Hook] Condition check (params._gl && !savedRef.current):', params._gl && !savedRef.current);

    // Check if _gl parameter exists
    if (params._gl && !savedRef.current) {
      //console.log('🟢 [GA Tracking Hook] ===== CONDITION MET - PROCEEDING TO SAVE =====');
      //console.log('🟢 [GA Tracking Hook] Setting gaData state...');
      setGaData(params);
      //console.log('🟢 [GA Tracking Hook] gaData state set to:', params);

      // Save all params to sessionStorage for persistence
      sessionStorage.setItem('ga_all_params', JSON.stringify(params));
      //console.log('🟢 [GA Tracking Hook] Saved all params to sessionStorage');

      // Prepare data for saving
      const dataToSave = {
        ...params,
        landing_url: window.location.href
      };
      //console.log('🟢 [GA Tracking Hook] Data to save:', dataToSave);
      //console.log('🟢 [GA Tracking Hook] Calling saveGoogleAnalytics function...');

      // Save to backend
      saveGoogleAnalytics(dataToSave)
        .then((response) => {
          //console.log('🟢 [GA Tracking Hook] ===== saveGoogleAnalytics PROMISE RESOLVED =====');
          //console.log('🟢 [GA Tracking Hook] Response received:', response);
          //console.log('🟢 [GA Tracking Hook] Response code:', response?.code);
          //console.log('🟢 [GA Tracking Hook] Response message:', response?.message);

          if (response.code === 200) {
            //console.log('🟢 [GA Tracking Hook] ✅ SUCCESS - Response code is 200');
            setIsSaved(true);
            savedRef.current = true;
            // Mark as saved in sessionStorage
            sessionStorage.setItem('ga_tracking_saved', 'true');
            sessionStorage.setItem('ga_tracking_saved_at', Date.now().toString());
            //console.log('🟢 [GA Tracking Hook] State updated: isSaved=true, savedRef.current=true');
            //console.log('🟢 [GA Tracking Hook] Google Analytics tracking saved:', response);
          } else {
            console.error('🔴 [GA Tracking Hook] ❌ FAILED - Response code is not 200');
            console.error('🔴 [GA Tracking Hook] Failed to save Google Analytics:', response);
          }
        })
        .catch((error) => {
          console.error('🔴 [GA Tracking Hook] ===== saveGoogleAnalytics PROMISE REJECTED =====');
          console.error('🔴 [GA Tracking Hook] Error caught in hook:', error);
          console.error('🔴 [GA Tracking Hook] Error name:', error?.name);
          console.error('🔴 [GA Tracking Hook] Error message:', error?.message);
          console.error('🔴 [GA Tracking Hook] Error stack:', error?.stack);
        });
    } else {
      //console.log('🟡 [GA Tracking Hook] ⚠️ CONDITION NOT MET - SKIPPING SAVE');
      if (!params._gl) {
        //console.log('🟡 [GA Tracking Hook] Reason: _gl parameter is missing or empty');
        //console.log('🟡 [GA Tracking Hook] All extracted params:', params);
      }
      if (savedRef.current) {
        //console.log('🟡 [GA Tracking Hook] Reason: Already saved (savedRef.current = true)');
      }
    }

    //console.log('🟢 [GA Tracking Hook] ===== EFFECT COMPLETED =====');
  }, [location.search]); // Only re-run when search params change

  return { gaData, isSaved };
}